/**
 * SOUND-643 — build the registration's status history into timeline entries.
 *
 * Mirrors the shape `components/Tickets/ActivityTimeline` consumes, so the
 * registration record reads the same way a ticket does: a dot rail, oldest at
 * the top, one line per thing that happened.
 *
 * Pure and tested because the component that draws it cannot be — sound-app has
 * no component-test harness.
 */

export const REGISTRATION_STATUS_LABELS = {
  new: "New",
  in_progress: "In Progress",
  submitted: "Submitted",
  approved: "Approved",
  rejected: "Rejected",
  needs_info: "Needs Info",
};

/**
 * Dot colour per destination status. Deliberately the same semantic tokens the
 * ticket timeline uses, so "green means done, amber means waiting on someone,
 * red means refused" holds across both surfaces.
 */
export function dotForStatus(status) {
  switch (status) {
    case "approved":
      return "bg-success";
    case "rejected":
      return "bg-destructive";
    case "needs_info":
      return "bg-warning";
    case "submitted":
    case "in_progress":
      return "bg-info";
    case "new":
    default:
      return "bg-brand";
  }
}

export function statusLabel(status) {
  return REGISTRATION_STATUS_LABELS[status] || status || "—";
}

/**
 * Everything that happened to this registration, as one chronological rail.
 *
 * Creation and submission are ENTRIES, not a separate meta block above the rail.
 * Showing "Created — August 13" next to a rail item reading "Application
 * created · 11h ago" states the same fact twice in two visual languages.
 *
 * @param {object} application
 * @returns {Array<{key,dot,text,reason,actor,at}>} oldest first
 */
export function buildRegistrationTimeline(application) {
  if (!application) return [];
  const entries = [];

  if (application.createdAt) {
    entries.push({
      key: "created",
      dot: "bg-brand",
      text: "Application created",
      reason: null,
      actor: null,
      at: application.createdAt,
    });
  }

  const history = Array.isArray(application.statusHistory)
    ? application.statusHistory
    : [];

  history.forEach((h, i) => {
    entries.push({
      key: `h-${i}`,
      dot: dotForStatus(h.to),
      // `from` is absent on the oldest entries written before the field existed,
      // so render the destination alone rather than "undefined → Submitted".
      text: h.from
        ? `${statusLabel(h.from)} → ${statusLabel(h.to)}`
        : statusLabel(h.to),
      reason: h.reason || null,
      actor: h.changedBy?.name || h.changedBy?.email || null,
      at: h.changedAt || null,
    });
  });

  // 71 of 334 production applications have a `submittedAt` but NO statusHistory
  // — they predate the history field. Without this, the single most significant
  // event in the record's life is missing from its timeline. Skipped when a
  // history entry already records the submission, so it never doubles up.
  const submissionInHistory = history.some((h) => h.to === "submitted");
  if (application.submittedAt && !submissionInHistory) {
    entries.push({
      key: "submitted",
      dot: dotForStatus("submitted"),
      text: "Submitted",
      reason: null,
      actor: null,
      at: application.submittedAt,
    });
  }

  return entries.sort((a, b) => {
    // An entry with no timestamp must keep its INSERTION position — older
    // history rows predate `changedAt`, and a record's own creation should not
    // be shoved below them. Returning 0 leaves the pair untouched (Array.sort
    // is stable); returning -1/1 here would sort undated entries to one end.
    if (!a.at || !b.at) return 0;
    return new Date(a.at) - new Date(b.at);
  });
}
